import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../services/api';
import axios from 'axios';
import './AdminChatDashboard.css';

interface SupportChat {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: 'active' | 'waiting' | 'resolved';
  createdAt: string;
  lastMessage: string;
  unreadCount: number;
  assignedTo?: string;
  messages: Array<{
    sender: string;
    message: string;
    timestamp: string;
  }>;
}

const AdminChatDashboard: React.FC = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<SupportChat | null>(null);
  const [messages, setMessages] = useState<Array<{sender: string, message: string, timestamp: string}>>([]);
  const [inputText, setInputText] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'waiting' | 'resolved'>('active');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchChats();
    const interval = setInterval(fetchChats, 3000);
    return () => clearInterval(interval);
  }, [user?.isAdmin]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat._id);
      const interval = setInterval(() => fetchMessages(selectedChat._id), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedChat]);

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setChats(response.data.chats);
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId: string) => {
    try {
      console.log('Fetching messages for chat:', chatId);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Messages response:', response.data);
      if (response.data.success && response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !selectedChat) return;
    const messageText = inputText;
    setInputText('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/support/${selectedChat._id}/message`, {
        message: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(() => fetchMessages(selectedChat._id), 500);
      setTimeout(() => fetchChats(), 500);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const assignChatToMe = async (chatId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/support/${chatId}/assign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChats();
    } catch (error) {
      console.error('Error assigning chat:', error);
    }
  };

  const resolveChat = async (chatId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/support/${chatId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChats();
      if (selectedChat?._id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error resolving chat:', error);
    }
  };

  const selectChat = (chat: SupportChat) => {
    setSelectedChat(chat);
    setMessages([]);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <span className="status-badge active">Active</span>;
      case 'waiting': return <span className="status-badge waiting">Waiting</span>;
      case 'resolved': return <span className="status-badge resolved">Resolved</span>;
      default: return null;
    }
  };

  const filteredChats = chats.filter(chat => {
    if (filter === 'all') return true;
    return chat.status === filter;
  });

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-chat-access-denied">
        <h3>Access Denied</h3>
        <p>You need administrator privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading chats...</p>
      </div>
    );
  }

  return (
    <div className="admin-chat-dashboard">
      <div className="dashboard-header">
        <h2>📞 Live Support Dashboard</h2>
        <div className="connection-status">
          <span className="status-dot connected"></span>
          Live (Auto-refresh: 3s)
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-value">{chats.filter(c => c.status === 'active').length}</div>
          <div className="stat-label">Active Chats</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{chats.filter(c => c.status === 'waiting').length}</div>
          <div className="stat-label">Waiting</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{chats.filter(c => c.status === 'resolved').length}</div>
          <div className="stat-label">Resolved</div>
        </div>
      </div>

      <div className="dashboard-filters">
        <button className={`filter-btn ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>
          Active ({chats.filter(c => c.status === 'active').length})
        </button>
        <button className={`filter-btn ${filter === 'waiting' ? 'active' : ''}`} onClick={() => setFilter('waiting')}>
          Waiting ({chats.filter(c => c.status === 'waiting').length})
        </button>
        <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All ({chats.length})
        </button>
      </div>

      <div className="dashboard-main">
        <div className="chat-list">
          {filteredChats.map(chat => (
            <div key={chat._id} className={`chat-list-item ${selectedChat?._id === chat._id ? 'selected' : ''}`} onClick={() => selectChat(chat)}>
              <div className="chat-info">
                <div className="chat-user">{chat.userName}</div>
                <div className="chat-email">{chat.userEmail}</div>
                <div className="chat-last-message">{chat.lastMessage || 'No messages'}</div>
              </div>
              <div className="chat-meta">
                {getStatusBadge(chat.status)}
                {chat.unreadCount > 0 && <span className="unread-badge">{chat.unreadCount}</span>}
              </div>
            </div>
          ))}
          {filteredChats.length === 0 && <div className="no-chats"><p>No chats found</p></div>}
        </div>

        <div className="chat-window">
          {selectedChat ? (
            <>
              <div className="chat-window-header">
                <div>
                  <strong>{selectedChat.userName}</strong>
                  <small>{selectedChat.userEmail}</small>
                  <span className={`chat-status ${selectedChat.status}`}>{selectedChat.status.toUpperCase()}</span>
                </div>
                <div className="chat-actions">
                  {selectedChat.status === 'waiting' && (
                    <button onClick={() => assignChatToMe(selectedChat._id)} className="assign-btn">Take Chat</button>
                  )}
                  {selectedChat.status !== 'resolved' && (
                    <button onClick={() => resolveChat(selectedChat._id)} className="resolve-btn">Resolve</button>
                  )}
                </div>
              </div>

              <div className="chat-messages">
                {messages.length === 0 ? (
                  <div className="no-messages"><p>No messages yet. Be the first to respond!</p></div>
                ) : (
                  messages.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.sender === 'admin' ? 'admin' : 'user'}`}>
                      <div className="message-bubble">
                        <div className="message-text">{msg.message}</div>
                        <div className="message-time">{formatTime(msg.timestamp)}</div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type your response..."
                  rows={2}
                />
                <button onClick={sendMessage} disabled={!inputText.trim()}></button>
              </div>
            </>
          ) : (
            <div className="no-chat-selected"><p>Select a chat to start assisting customers</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChatDashboard;



